import {
  CSSTransition,
  TransitionGroup
} from 'react-transition-group'
import type { CSSTransitionProps } from 'react-transition-group/CSSTransition'
import { PropsWithChildren, useRef, type JSX } from 'react'
import './double-calendar.scss'
import classNames from 'classnames'
import Calendar, { CalendarProps } from './calendar'

const SLIDE_TIMEOUT_MS = 200

/** Transition state props that `TransitionGroup` injects onto its direct children. */
type CalendarSlideProps = Omit<CSSTransitionProps<HTMLDivElement>, 'nodeRef' | 'classNames' | 'timeout' | 'addEndListener' | 'children'>

/**
 * One animated calendar pair. Owns its own `nodeRef` because an entering and a
 * leaving slide coexist inside the `TransitionGroup`, so a shared ref would be wrong.
 */
function CalendarSlide ({ children, ...transitionProps }: PropsWithChildren<CalendarSlideProps>): JSX.Element {
  const nodeRef = useRef<HTMLDivElement>(null)
  return (
    <CSSTransition {...transitionProps} nodeRef={nodeRef} classNames='slide' timeout={SLIDE_TIMEOUT_MS}>
      <div ref={nodeRef} className='grid gap-8 grid-cols-2 duration-200'>{children}</div>
    </CSSTransition>
  )
}

export default function DoubleCalendar (props: CalendarProps): JSX.Element {
  const prevValue = useRef(0)
  const animationClass = useRef<'slide-left' | 'slide-right'>('slide-left')

  const _year: number = props.month.year()
  const _month: number = props.month.month()
  const value = _year * 100 + _month
  const secondMonth = props.month.add(1, 'month')

  if (prevValue.current < value) {
    animationClass.current = 'slide-left'
    prevValue.current = value
  } else if (prevValue.current > value) {
    animationClass.current = 'slide-right'
    prevValue.current = value
  }

  return (
    <TransitionGroup component='div' className={classNames('flex gap-8 grid double-calendar', animationClass.current)}>
      <CalendarSlide key={value}>
        <Calendar {...props} showNext={false} />
        <Calendar {...props} month={secondMonth} showPrevious={false} />
      </CalendarSlide>
    </TransitionGroup>
  )
}
